import {
  Ship,
  DollarSign,
  TrendingUp,
  Clock,
  FileText,
  MapPin,
  Anchor,
  Building2,
  Gauge,
} from 'lucide-react';
import { VesselBadge } from './VesselBadge';
import { FixtureVesselSizeBadge } from './FixtureVesselSizeBadge';
import { FixtureStatus } from './FixtureStatus';
import { Fixture, RecentFixture } from '@/types/fixtures';
import {
  formatCargoSize,
  getStatusVariant,
  formatDate,
  formatRate,
  daysBetween,
  formatLayCan,
} from '@/utils/fixtureUtils';

interface FixtureDetailsViewProps {
  fixture: Fixture;
  isRecentFixture?: boolean;
}

export default function FixtureDetailsView({
  fixture,
  isRecentFixture = false,
}: FixtureDetailsViewProps) {
  const formatDecimal = (value: number | null | undefined, decimals = 1) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '--';
    }
    return value.toFixed(decimals);
  };

  const laycanDuration = daysBetween(fixture.layCanStart, fixture.layCanEnd);
  const statusVariant = getStatusVariant(fixture.status);
  const vesselYear =
    fixture.vesselYear ??
    (Number.isFinite(fixture.age) && fixture.age >= 0
      ? new Date().getFullYear() - fixture.age
      : undefined);
  const shouldShowVesselYear = !isRecentFixture && vesselYear !== null && vesselYear !== undefined;

  const statusBadgeClass =
    statusVariant === 'success'
      ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950'
      : statusVariant === 'primary'
        ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950'
        : statusVariant === 'error'
          ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950'
          : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900';

  return (
    <div className="fixture-details-page px-4 md:px-0">
      {/* Hero Section - Blue Gradient */}
      <div className="mb-4 overflow-visible rounded-2xl bg-gradient-to-r from-blue-600 to-blue-500 p-6 shadow-xl">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1">
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <Ship className="h-8 w-8 text-white" />
              <h1 className="text-3xl font-bold text-white">
                {fixture.vesselName}
                {shouldShowVesselYear && (
                  <span className="ml-2 text-2xl font-normal opacity-70">({vesselYear})</span>
                )}
              </h1>
              <div
                className={`inline-flex items-center justify-center rounded-full border px-3 py-2 shadow-sm ${statusBadgeClass}`}
                aria-label={`${fixture.status} status`}
              >
                <FixtureStatus status={fixture.status} className="text-sm leading-none" />
              </div>
              <VesselBadge
                isOwnFixture={!!fixture.isOwnFixture}
                scrubberInstalled={fixture.scrubberInstalled}
                age15Plus={fixture.age >= 15}
              />
            </div>

            <div className="mb-2 flex flex-wrap items-center gap-4 text-sm text-white">
              {isRecentFixture && (
                <FixtureVesselSizeBadge vesselSizeId={(fixture as RecentFixture).vesselSizeId} />
              )}
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                <span>{fixture.operator}</span>
              </div>
              <div className="flex items-center gap-2">
                <Anchor className="h-4 w-4" />
                <span>{fixture.type}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                <span>{fixture.trade}</span>
              </div>
            </div>

            <p className="text-xs text-blue-100">
              Fixture ID: #{fixture.fixtureId} • Reported:{' '}
              {formatDate(fixture.reportedTime, 'MMM d, y, h:mm a')}
            </p>
          </div>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white/5 p-4 backdrop-blur dark:border-slate-700/50 dark:bg-slate-800/50">
          <div className="mb-1 text-xs text-gray-500 dark:text-slate-400">Cargo Size</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {Math.round(fixture.cargoSize / 1000)}K
          </div>
          <div className="text-sm text-gray-500 dark:text-slate-400">mts</div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white/5 p-4 backdrop-blur dark:border-slate-700/50 dark:bg-slate-800/50">
          <div className="mb-1 text-xs text-gray-500 dark:text-slate-400">Rate</div>
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {fixture.rate ? formatRate(fixture.rate, fixture.rateType) : '--'}
          </div>
          <div className="text-sm text-gray-500 dark:text-slate-400">
            {fixture.rateType === 'ws' ? 'Worldscale' : 'Lumpsum'}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white/5 p-4 backdrop-blur dark:border-slate-700/50 dark:bg-slate-800/50">
          <div className="mb-1 text-xs text-gray-500 dark:text-slate-400">Laycan Period</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{laycanDuration}</div>
          <div className="text-sm text-gray-500 dark:text-slate-400">days</div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white/5 p-4 backdrop-blur dark:border-slate-700/50 dark:bg-slate-800/50">
          <div className="mb-1 text-xs text-gray-500 dark:text-slate-400">Vessel Age</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{fixture.age}</div>
          <div className="text-sm text-gray-500 dark:text-slate-400">years</div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-4 md:grid-cols-3">
        {/* Left Column - 2 cols */}
        <div className="space-y-4 md:col-span-2">
          {/* Charter Details */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm backdrop-blur dark:border-slate-700/50 dark:bg-slate-800/50">
            <div className="mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Charter Details
              </h2>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <div className="mb-1 text-sm text-gray-600 dark:text-slate-400">Charterer</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {fixture.charterer}
                </div>
              </div>

              <div>
                <div className="mb-1 text-sm text-gray-600 dark:text-slate-400">Laycan Period</div>
                <div className="font-semibold text-gray-900 dark:text-white">
                  {formatLayCan(fixture.layCanStart, fixture.layCanEnd)},{' '}
                  {formatDate(fixture.layCanEnd, 'yyyy')}
                </div>
                <div className="text-sm text-gray-600 dark:text-slate-400">
                  ({laycanDuration} days)
                </div>
              </div>

              <div>
                <div className="mb-1 text-sm text-gray-600 dark:text-slate-400">Trade Route</div>
                <div className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white">
                  <MapPin className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  {fixture.trade}
                </div>
              </div>

              <div>
                <div className="mb-1 text-sm text-gray-600 dark:text-slate-400">Rate</div>
                <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                  {fixture.rate ? formatRate(fixture.rate, fixture.rateType) : '--'}
                </div>
              </div>

              <div>
                <div className="mb-1 text-sm text-gray-600 dark:text-slate-400">Cargo Size</div>
                <div className="font-semibold text-gray-900 dark:text-white">
                  {formatCargoSize(fixture.cargoSize)}
                </div>
              </div>
            </div>
          </div>

          {/* Financial & Performance in 2 columns */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Financial Details */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm backdrop-blur dark:border-slate-700/50 dark:bg-slate-800/50">
              <div className="mb-4 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Financial</h2>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-slate-400">
                    Primary Broker Commission
                  </span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {fixture.primaryBrokerCommission
                      ? (fixture.primaryBrokerCommission * 100).toFixed(2) + '%'
                      : '--'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-slate-400">
                    Secondary Broker Commission
                  </span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {fixture.secondaryBrokerCommission
                      ? (fixture.secondaryBrokerCommission * 100).toFixed(2) + '%'
                      : '--'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-slate-400">
                    Address Commission
                  </span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {fixture.addressCommission
                      ? (fixture.addressCommission * 100).toFixed(2) + '%'
                      : '--'}
                  </span>
                </div>
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm backdrop-blur dark:border-slate-700/50 dark:bg-slate-800/50">
              <div className="mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Performance</h2>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-slate-400">Idle Days</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {formatDecimal(fixture.idleDays)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-slate-400">Laytime</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {fixture.laytime}h
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-slate-400">
                    Actual TCE Include Idle
                  </span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {formatDecimal(fixture.tcrtInclIdleDays)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Speed & Consumption */}
          {(fixture.ballastSpeed ||
            fixture.ladenSpeed ||
            fixture.ballastConsumption ||
            fixture.ladenConsumption) && (
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm backdrop-blur dark:border-slate-700/50 dark:bg-slate-800/50">
              <div className="mb-4 flex items-center gap-2">
                <Gauge className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Speed & Consumption
                </h2>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <div className="mb-3 text-sm font-semibold text-gray-700 dark:text-slate-300">
                    Ballast Cons/Speed
                  </div>
                  <div className="space-y-2">
                    {fixture.ballastSpeed && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-slate-400">Speed</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {formatDecimal(fixture.ballastSpeed)} knots
                        </span>
                      </div>
                    )}
                    {fixture.ballastConsumption && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-slate-400">
                          Consumption
                        </span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {formatDecimal(fixture.ballastConsumption)} mt/day
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <div className="mb-3 text-sm font-semibold text-gray-700 dark:text-slate-300">
                    Laden Cons/Speed
                  </div>
                  <div className="space-y-2">
                    {fixture.ladenSpeed && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-slate-400">Speed</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {formatDecimal(fixture.ladenSpeed)} knots
                        </span>
                      </div>
                    )}
                    {fixture.ladenConsumption && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-slate-400">
                          Consumption
                        </span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {formatDecimal(fixture.ladenConsumption)} mt/day
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-4">
          {/* Quick Stats */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm backdrop-blur dark:border-slate-700/50 dark:bg-slate-800/50">
            <div className="mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Quick Stats</h2>
            </div>

            <div className="space-y-3">
              <div>
                <div className="mb-1 text-sm text-gray-600 dark:text-slate-400">Reported</div>
                <div className="font-semibold text-gray-900 dark:text-white">
                  {formatDate(fixture.reportedTime, 'MMM d, y, h:mm a')}
                </div>
              </div>

              <div>
                <div className="mb-1 text-sm text-gray-600 dark:text-slate-400">Own Fixture</div>
                <div className="font-semibold text-gray-900 dark:text-white">
                  {fixture.isOwnFixture === 1 ? 'Yes' : 'No'}
                </div>
              </div>
            </div>
          </div>

          {/* Broker Information */}
          {fixture.broker && (
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm backdrop-blur dark:border-slate-700/50 dark:bg-slate-800/50">
              <div className="mb-4 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Broker</h2>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="mb-1 text-sm text-gray-600 dark:text-slate-400">Company</div>
                  <div className="text-sm font-semibold leading-tight text-gray-900 dark:text-white">
                    {fixture.broker}
                  </div>
                </div>

                <div>
                  <div className="mb-1 text-sm text-gray-600 dark:text-slate-400">
                    Did Not Share
                  </div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {fixture.brokerDidNotShare ? 'Yes' : 'No'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Additional Notes */}
          {(fixture.note || fixture.rateExplanation) && (
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm backdrop-blur dark:border-slate-700/50 dark:bg-slate-800/50">
              <div className="mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Notes</h2>
              </div>

              {fixture.note && (
                <div className="mb-4">
                  <div className="mb-1 text-sm text-gray-600 dark:text-slate-400">General Note</div>
                  <div className="rounded-lg bg-gray-50 p-3 dark:bg-slate-700/50">
                    <p className="text-sm text-gray-700 dark:text-gray-300">{fixture.note}</p>
                  </div>
                </div>
              )}
              {fixture.rateExplanation && (
                <div>
                  <div className="mb-1 text-sm text-gray-600 dark:text-slate-400">Rate Details</div>
                  <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      {fixture.rateExplanation}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
